/**
 * useInvitationMetadata / useAcceptInvite — TanStack Query hooks for the
 * W1a-06 invitation accept flow.
 *
 * Both hooks talk to the PUBLIC /api/v1/auth endpoints — they bypass the
 * shared apiClient because the user accepting the invitation is, by
 * definition, NOT yet authenticated. We use raw `fetch` (same pattern
 * as routes/login.tsx).
 *
 * Endpoint asymmetry to be aware of:
 *   - GET /auth/invitation/:token returns 404 NOT_FOUND for any read
 *     failure (no row, expired, non-pending). This is a status read with
 *     no state-machine semantic so 404 is the natural shape.
 *   - POST /auth/accept-invite returns 410 GONE for any redemption
 *     failure (token unknown, expired, revoked, already redeemed). The
 *     server uses a SINGLE generic message to prevent token-enumeration
 *     via response-text differentiation.
 */

import { useQuery, useMutation } from '@tanstack/react-query';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface InvitationMetadata {
  email: string;
  organizationName: string;
  invitedByName: string;
  /** ISO 8601 expiry timestamp */
  expiresAt: string;
  /** Always false in v1 — F-W1a-06.1 enables real MFA enrollment. */
  requiresMfa: boolean;
}

export interface AcceptInviteBody {
  token: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface AcceptInviteResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    /** Null when the invitee holds no shipped platform role in the tenant. */
    role: string | null;
  };
  /** Always null in v1 — F-W1a-06.1 follow-up will surface TOTP enrollment. */
  mfaEnrollment: null;
}

/**
 * Error thrown by the invitation hooks. Surfaces both the HTTP status
 * (so the page can switch between "expired" / "rate-limited" / "form
 * validation" branches) and the server's error code for the rare cases
 * where the page wants to render a code-specific message.
 */
export class InvitationApiError extends Error {
  status: number;
  code: string;
  retryAfterMs?: number;

  constructor(
    message: string,
    status: number,
    code: string,
    retryAfterMs?: number
  ) {
    super(message);
    this.name = 'InvitationApiError';
    this.status = status;
    this.code = code;
    this.retryAfterMs = retryAfterMs;
  }
}

// ─── Internal fetch helper ──────────────────────────────────────────────────

async function publicFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`/api/v1${path}`, { ...init, headers });

  if (!response.ok) {
    let payload: { error?: { code?: string; message?: string; retryAfterMs?: number } } = {};
    try {
      payload = await response.json();
    } catch {
      // Fall through with empty payload.
    }
    throw new InvitationApiError(
      payload.error?.message ?? `Request failed: ${response.status}`,
      response.status,
      payload.error?.code ?? 'UNKNOWN_ERROR',
      payload.error?.retryAfterMs
    );
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetches the invitation metadata (email, org name, inviter, expiry) so
 * the accept page can render context. On 404 the page should render an
 * "invitation expired or already used" friendly error — DO NOT retry.
 *
 * The hook deliberately disables retries because:
 *   - Network blips on a public route are rare; better to surface the
 *     error fast than spin a loading state.
 *   - Token-enumeration probes shouldn't be amplified by retry loops.
 *
 * @param token - The raw invitation token from the URL `/invite/:token`.
 *                Empty / null / undefined disables the query.
 */
export function useInvitationMetadata(token: string | undefined | null) {
  return useQuery<InvitationMetadata, InvitationApiError>({
    queryKey: ['invitation', token],
    queryFn: () =>
      publicFetch<InvitationMetadata>(
        `/auth/invitation/${encodeURIComponent(token!)}`
      ),
    enabled: Boolean(token),
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Redeems the invitation token and activates the account. On 200 the
 * caller MUST persist accessToken + refreshToken to localStorage (same
 * scheme as /auth/login) and navigate to `/`. On 410 surface a friendly
 * "this invitation is no longer valid" message. On 422 the password
 * failed Zod validation — show field-level errors.
 *
 * The hook does NOT auto-store the tokens — that's the page's
 * responsibility (matches /auth/login pattern, keeps the hook reusable
 * if a future flow wants to do something other than auto-login).
 */
export function useAcceptInvite() {
  return useMutation<AcceptInviteResponse, InvitationApiError, AcceptInviteBody>({
    mutationFn: (body) =>
      publicFetch<AcceptInviteResponse>('/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
